import { useContext, useState, useEffect, useRef, useMemo } from "react";
import Link from "next/link";
import Head from "next/head";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, Configure, useRefinementList } from "react-instantsearch-hooks-web";
import StoreContext from "@/context/store";
import Header from "@/components/shop/Header";
import MessageXmark from "/packages/icons/message-xmark.svg";
import styles from "./Brands.module.scss";
import { useSiteSettings } from "@/context/siteSettings";
import Xmark from "/packages/icons/xmark.svg";
import { getSiteSettings } from '@/lib/getSiteSettings';

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_KEY
);

export async function getStaticProps() {
  const siteSettings = await getSiteSettings();
  return {
    props: {
      siteSettings,
    },
    revalidate: 60,
  };
}

const fetchBrands = async (store) => {
  const productsIndex = searchClient.initIndex("menu-products-production");
  const brandsResponse = await productsIndex.search("", {
    filters: `store_id:${store}`,
    facets: ["brand"],
  });

  const brandNames = Object.keys(brandsResponse.facets?.brand || []);
  if (!brandNames.length) return [];

  const brandsIndex = searchClient.initIndex("product-brand-production");
  const janeBrandsResponse = await brandsIndex.search("", {
    filters: brandNames.map((name) => `name:"${name}"`).join(" OR "),
    hitsPerPage: 200,
  });

  return janeBrandsResponse.hits;
};

export default function BrandsPage() {
  const { store } = useContext(StoreContext);
  const [brandsData, setBrandsData] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsFetching(true);
      try {
        const data = await fetchBrands(store);
        setBrandsData(data);
      } catch (error) {
        console.error("Error fetching brands:", error);
        setBrandsData([]);
      } finally {
        setIsFetching(false);
      }
    };

    if (store) fetchData();
  }, [store]);

  return (
    <div className={styles.brands}>
      <InstantSearch indexName={process.env.NEXT_PUBLIC_ALGOLIA_INDEX} searchClient={searchClient}>
        <Configure filters={`store_id:${store}`} attributesToHighlight={["brand"]} />
        <BrandsRefinementList attribute="brand" sortBy={["name:asc"]} limit={200} brands={brandsData} />
      </InstantSearch>
    </div>
  );
}

function BrandsRefinementList({ attribute, sortBy, limit, brands }) {
  const { items, searchForItems, isFromSearch } = useRefinementList({ attribute, sortBy, limit });
  const [searchState, setSearchState] = useState({ query: "", isFocused: false });
  const searchBoxRef = useRef(null);
  const { featuredBrands = [] } = useSiteSettings();

  const filteredItems = useMemo(() => {
    const featured = featuredBrands.map((b) => b.toLowerCase());

    return [
      ...items.filter((item) => featured.includes(item.label.toLowerCase())),
      ...items.filter((item) => !featured.includes(item.label.toLowerCase())),
    ];
  }, [items, featuredBrands]);

  const handleInputChange = (e) => {
    const query = e.target.value;

    // Ensure suggestions show again when typing starts
    if (!searchState.isFocused && query) {
      setSearchState((prev) => ({ ...prev, query, isFocused: true }));
    } else {
      setSearchState((prev) => ({ ...prev, query }));
    }

    searchForItems(query);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchState((prev) => ({ ...prev, isFocused: false })); // Close the search box
    searchBoxRef.current?.blur(); // Ensure the input loses focus
    searchForItems(searchState.query); // Trigger the search explicitly
  };

  const handleSuggestionClick = (item) => {
    setSearchState({ query: item.label, isFocused: false });
    searchForItems(item.value);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setSearchState((prev) => ({ ...prev, isFocused: false }));
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const abbreviateLabel = (label) =>
    label
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase();

  return (
    <>
      <header>
        <Head>
          <title>Cannabis Brands | Purple Lotus</title>
          <meta property="og:title" content="Cannabis Brands | Purple Lotus" />
          <meta name="description" content="Explore all cannabis brands available at Purple Lotus, San Jose’s top dispensary. Discover premium flower, edibles, vapes, and more from trusted names." />
        </Head>
      </header>

      <main className="container pb-3">
      <Header category="brands" />
        <div className="searchBox relative" ref={searchBoxRef}>
          <form onSubmit={handleSubmit}>
            <input
              placeholder="Search for brands"
              value={searchState.query}
              onChange={handleInputChange}
              onFocus={() => setSearchState((prev) => ({ ...prev, isFocused: true }))}
            />
            {searchState.query && (
              <button
                type="button"
                onClick={() => {
                  setSearchState({ query: "", isFocused: false });
                  searchForItems(""); // Clear Algolia filter
                }}
                className="clearButton"
                aria-label="Clear search"
              >
                <Xmark />
              </button>
            )}
          </form>
          {searchState.query && searchState.isFocused && (
            <ul className={styles.autocompleteResults}>
              {items.map((item) => (
                <li key={item.label} onClick={() => handleSuggestionClick(item)}>
                  {item.label}
                </li>
              ))}
            </ul>
          )}
        </div>
  
        {items.length ? (
          <ul className={styles.brandGrid}>
            {filteredItems.map((item) => {
              const brandData = brands.find((brand) => brand.name === item.label);
              return (
                <li key={item.label}>
                  <Link href={`/brands/${item.label.replace(/ /g, "-")}`} passHref prefetch={false}>
                    <div className={styles.logo}>
                      {brandData?.logo_url ? (
                        <img
                          src={brandData.logo_url}
                          alt={item.label}
                          style={{ objectFit: "contain", width: "100%", height: "auto" }}
                        />
                      ) : (
                        abbreviateLabel(item.label)
                      )}
                    </div>
                    <h2 className="fs-500 fw-semi-bold">{item.label}</h2>
                    <p className="fs-300">
                      {item.count} product{item.count > 1 ? "s" : ""}
                    </p>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          isFromSearch && (
            <div className={styles.no_results}>
              <MessageXmark style={{ fontSize: '3rem' }} />
              No results for &ldquo;{searchState.query}&rdquo;
            </div>
          )
        )}
      </main>
    </>
  );
}
